import type { FC } from "react";
import {
  IconNameEnum,
  useFlightStyles,
  useModuleBEM,
} from "@b2bportal/core-themes";
import {
  CoreFlightsComponent,
  type FlightFareViewProps,
} from "@b2bportal/core-types";
import { isMixedCabin } from "@b2bportal/core-utilities";
import { useI18nContext } from "@hopper-b2b/i18n";
import {
  getSeatsRemaining,
  shouldShowSeatsRemaining,
  useGetFixedPricingFromFare,
} from "@overrides/utilities";
import { FareDetails } from "@tenant/flights";
import { Icon, Tooltip, usePriceDisplay } from "@tenant/ui";
import defaultStyles from "./FlightFareView.module.scss";
import { useIsPackageIncrementalPricing } from "@/pages/Packages/hooks";

const COMPONENT_KEY = CoreFlightsComponent.FlightFareView;

export const FlightFareView: FC<FlightFareViewProps> = ({
  fareDetails,
  tripDetails,
  sliceIndex,
  onSelect,
}) => {
  const styles = useFlightStyles(COMPONENT_KEY, defaultStyles);
  const [, cn] = useModuleBEM(styles, COMPONENT_KEY);

  const { t } = useI18nContext();
  const getFixedPricingFromFare = useGetFixedPricingFromFare();
  const prices = getFixedPricingFromFare(fareDetails.paxPricings?.[0]);

  const isIncrementalPricing = useIsPackageIncrementalPricing();

  const { rewardsThenPriceText } = usePriceDisplay({
    prices,
    inline: "priceOnly",
    formatOptions: isIncrementalPricing ? { signDisplay: "always" } : undefined,
  });

  const seatsRemaining = getSeatsRemaining(fareDetails.slices[sliceIndex]);

  const showMixedCabin = isMixedCabin(fareDetails.slices[sliceIndex]);

  return (
    <>
      <div className={cn("fare-title")}>
        {fareDetails.slices[sliceIndex].fareShelf?.brandName ?? ""}
      </div>
      <div className={cn("fare-details")}>
        <FareDetails
          segments={tripDetails.slices[sliceIndex].segmentDetails}
          fareSlice={fareDetails.slices[sliceIndex]}
          restrictions={fareDetails.restrictions?.[sliceIndex]}
          // Update if we want to show more fare details at once
          numberOfFareDetails={6}
        />
      </div>
      {showMixedCabin && (
        <div className={cn("mixed-cabin")}>
          <Tooltip
            content={
              <p className={cn("tooltip-text")}>{t("mixedCabin.tooltip")}</p>
            }
            customIcon={
              <Icon
                className={cn("tooltip-icon")}
                iconName={IconNameEnum.info}
              />
            }
          >
            <p>{t("mixedCabin.title")}</p>
          </Tooltip>
        </div>
      )}
      <div className={cn("fare-select")}>
        {prices !== null && (
          <>
            {shouldShowSeatsRemaining(seatsRemaining) && (
              <p className={cn("seats-remaining")}>
                {t("seatsRemaining", { count: seatsRemaining })}
              </p>
            )}
            <p className={cn("continue-price")}>{rewardsThenPriceText}</p>
            <button className={cn("fare-button")} onClick={onSelect}>
              {t("continue")}
            </button>
          </>
        )}
      </div>
    </>
  );
};
